import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../app/AuthProvider";
import { useMe, useDecaReport, useUsers, usePlatformStats } from "../api/hooks";
import {
  StatCard,
  Card,
  Chip,
  ErrorNotice,
  PageHeader,
  Spinner,
} from "../components/ui";
import {
  IconDocument,
  IconUsers,
  IconWarning,
  IconChecklist,
  IconOffice,
  IconChart,
} from "../components/icons";
import { decaStatusLabel, decaStatusTone, formatDateTime } from "../lib/format";

// ---------------------------------------------------------------------------
// Dashboard de plataforma (super-admin sin tenant activo)
// ---------------------------------------------------------------------------

const SUB_STATUS_LABELS: Record<string, string> = {
  TRIAL: "Prueba",
  ACTIVE: "Activa",
  PENDING_PAYMENT: "Pago pendiente",
  BLOCKED: "Bloqueada",
  CANCELLED: "Cancelada",
};

const SUB_STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  TRIAL: "neutral",
  ACTIVE: "ok",
  PENDING_PAYMENT: "warn",
  BLOCKED: "danger",
  CANCELLED: "neutral",
};

function PlatformDashboard() {
  const stats = usePlatformStats();

  if (stats.isError) {
    return <ErrorNotice error={stats.error} onRetry={() => void stats.refetch()} />;
  }

  const s = stats.data;

  return (
    <>
      <PageHeader
        eyebrow="Plataforma"
        title="Dashboard de plataforma"
        description="Visión global de todos los tenants, suscripciones y actividad del sistema."
      />

      {stats.isLoading ? (
        <Spinner label="Cargando estadísticas de plataforma" />
      ) : (
        <>
          {/* Fila 1 — Tenants */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              accent="brand"
              icon={<IconOffice />}
              value={s?.totalTenants ?? 0}
              label="Tenants totales"
              hint={`${s?.activeTenants ?? 0} activos`}
            />
            <StatCard
              accent="teal"
              icon={<IconUsers />}
              value={s?.totalUsers ?? 0}
              label="Usuarios totales"
              hint="Todos los tenants"
            />
            <StatCard
              accent="violet"
              icon={<IconDocument />}
              value={s?.totalDocuments ?? 0}
              label="DeCA emitidos"
              hint={`${s?.documentsLast30Days ?? 0} últimos 30 días`}
            />
            <StatCard
              accent="amber"
              icon={<IconWarning />}
              value={s?.tenantsWithoutPlan ?? 0}
              label="Sin plan asignado"
              hint="Tenants sin suscripción"
            />
          </div>

          {/* Fila 2 — Suscripciones */}
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              accent="teal"
              icon={<IconChecklist />}
              value={s?.activeSubscriptions ?? 0}
              label="Suscripciones activas"
            />
            <StatCard
              accent="violet"
              icon={<IconChart />}
              value={s?.trialTenants ?? 0}
              label="En periodo de prueba"
            />
            <StatCard
              accent="amber"
              icon={<IconWarning />}
              value={(s?.subscriptionsByStatus?.["PENDING_PAYMENT"] ?? 0) + (s?.subscriptionsByStatus?.["BLOCKED"] ?? 0)}
              label="Con problemas de pago"
              hint="Pendiente + bloqueadas"
            />
          </div>

          {/* Desglose de suscripciones por estado */}
          {s && Object.keys(s.subscriptionsByStatus).length > 0 ? (
            <Card className="mt-4">
              <h2 className="micro-label mb-3">Desglose de suscripciones por estado</h2>
              <div className="flex flex-wrap gap-3">
                {Object.entries(s.subscriptionsByStatus).map(([status, count]) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 rounded-md border border-ink-200 px-3 py-2 dark:border-ink-700"
                  >
                    <Chip tone={SUB_STATUS_TONE[status] ?? "neutral"}>
                      {SUB_STATUS_LABELS[status] ?? status}
                    </Chip>
                    <span className="num text-sm font-semibold text-ink-800 dark:text-ink-100">
                      {count}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {/* Accesos rápidos */}
          <Card className="mt-4">
            <h2 className="micro-label mb-3">Acciones rápidas</h2>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/empresas"
                className="rounded-md bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
              >
                Gestionar tenants →
              </Link>
              <Link
                to="/suscripciones"
                className="rounded-md bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
              >
                Ver suscripciones →
              </Link>
              <Link
                to="/planes-facturacion"
                className="rounded-md bg-brand-50 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300 dark:hover:bg-brand-900/50"
              >
                Planes de facturación →
              </Link>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dashboard de tenant (admin/gestor/lector con tenant activo)
// ---------------------------------------------------------------------------

function TenantDashboard() {
  const { activeTenantId } = useAuth();
  const users = useUsers(activeTenantId);
  // Memoizado con activeTenantId como dependencia.
  // - No puede ir en el cuerpo sin memo: `new Date()` cambia cada render
  //   → queryKey inestable → React Query relanza el fetch en bucle infinito.
  // - No puede ir con deps vacías []: AppLayout nunca se desmonta, así que
  //   TenantDashboard tampoco se desmonta al cambiar de tenant. Con [] el
  //   valor queda fijado al primer tenant y la queryKey no cambia → React
  //   Query devuelve el resultado cacheado del tenant anterior sin refetchear.
  // - Con [activeTenantId]: cuando el superAdmin cambia de tenant, sevenDaysAgo
  //   se recalcula (nuevo timestamp), la queryKey cambia y React Query lanza
  //   un fetch nuevo con el token correcto del nuevo tenant.
  const sevenDaysAgo = useMemo(
    () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    [activeTenantId],
  );
  const report = useDecaReport({ dateFrom: sevenDaysAgo });

  if (report.isError)
    return <ErrorNotice error={report.error} onRetry={() => void report.refetch()} />;

  const summary = report.data?.summary;
  const recent = (report.data?.rows ?? []).slice(0, 6);

  return (
    <>
      <PageHeader
        eyebrow="Inicio"
        title="Dashboard"
        description="Resumen de los últimos 7 días. Para el histórico completo, usa Reportes."
      />

      {report.isLoading ? (
        <Spinner label="Cargando indicadores" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              accent="brand"
              icon={<IconDocument />}
              value={report.data?.total ?? 0}
              label="Documentos (7 días)"
              hint={`${summary?.byStatus?.ACTIVO ?? 0} activos`}
            />
            <StatCard
              accent="teal"
              icon={<IconUsers />}
              value={users.data?.items.length ?? 0}
              label="Usuarios activos"
            />
            <StatCard
              accent="violet"
              icon={<IconChecklist />}
              value={summary?.withCorrections ?? 0}
              label="Con correcciones"
              hint="Versiones o sustituciones"
            />
            <StatCard
              accent="amber"
              icon={<IconWarning />}
              value={typeof summary?.withIncidents === "number" ? summary.withIncidents : 0}
              label="Con incidencia"
              hint="Marcadas manualmente"
            />
          </div>

          <Card className="mt-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="micro-label">Últimos documentos emitidos</h2>
              <Link
                to="/deca"
                className="text-sm text-brand-700 hover:underline dark:text-brand-300"
              >
                Ver todos
              </Link>
            </div>
            {recent.length === 0 ? (
              <p className="py-8 text-center text-sm text-ink-500 dark:text-ink-400">
                No hay documentos emitidos en los últimos 7 días.
              </p>
            ) : (
              <ul className="divide-y divide-ink-100 dark:divide-ink-800">
                {recent.map((row) => (
                  <li
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2.5"
                  >
                    <Link
                      to={`/deca/${row.id}`}
                      className="ident text-sm text-brand-700 hover:underline dark:text-brand-300"
                    >
                      {row.urlToken}
                    </Link>
                    <span className="text-xs text-ink-500 dark:text-ink-400">
                      {formatDateTime(row.createdAt)}
                    </span>
                    <Chip tone={decaStatusTone(row.status)}>{decaStatusLabel(row.status)}</Chip>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Componente raíz: decide qué dashboard mostrar
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const auth = useAuth();
  const me = useMe();
  const isSuperAdmin = me.data?.isSuperAdmin ?? false;

  // Super-admin sin tenant activo → dashboard de plataforma
  if (isSuperAdmin && auth.needsTenant) {
    return <PlatformDashboard />;
  }

  return <TenantDashboard />;
}
